/**
 * Final CTA — gentle "float in on scroll" reveal for the two buttons.
 *
 * Plain script (no bundler). Loaded with `defer` from the section, which sits
 * earlier in the document than the GSAP libraries at the end of <body>; defer
 * scripts run in document order, so we wait for GSAP to be ready before init.
 *
 * A single one-shot ScrollTrigger fires as the section enters the viewport,
 * floating the buttons up and fading them in with a small stagger — the same
 * light, one-shot reveal the Interlude uses (see interlude.js).
 *
 * Respects prefers-reduced-motion and the theme editor: no tween — the buttons
 * are left in their composed rest state as the CSS renders them. Re-initialises
 * cleanly on editor section reloads.
 */
(function () {
  var triggers = []; // ScrollTriggers we create (for editor teardown)
  var tweens = []; // tweens we create

  function ready() {
    return window.gsap && window.ScrollTrigger;
  }

  function whenReady(cb) {
    if (ready()) return cb();
    // Poll until the GSAP libs (later in the document) have executed, so init
    // can never silently no-op if they aren't ready at DOMContentLoaded.
    var tries = 0;
    (function poll() {
      if (ready()) return cb();
      if (tries++ > 600) return; // ~10s safety cap
      requestAnimationFrame(poll);
    })();
  }

  function reset() {
    triggers.forEach(function (t) {
      t.kill();
    });
    tweens.forEach(function (t) {
      t.kill();
    });
    triggers = [];
    tweens = [];
    document.querySelectorAll('[data-final-cta]').forEach(function (s) {
      s.__finalCtaInit = false;
    });
  }

  function setup(section) {
    if (!section || section.__finalCtaInit) return;
    section.__finalCtaInit = true;

    var items = [].slice.call(section.querySelectorAll('[data-fc-float]'));
    if (!items.length) return;

    // "Static mode": no float-in tween. True under reduced-motion AND inside the
    // Shopify theme editor (Shopify.designMode), so editing shows the composed
    // rest state the CSS already renders.
    var staticMode =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !!(window.Shopify && window.Shopify.designMode);
    if (staticMode) return;

    gsap.set(items, { y: 36, autoAlpha: 0, filter: 'blur(6px)' });

    // Trigger off the centred content, not the tall (100svh) section: triggering
    // on the section top fires while the buttons are still below the fold, so the
    // one-shot tween finishes before they're ever seen.
    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: section.querySelector('.final-cta__inner') || section,
        start: 'top 85%',
        once: true,
        // Sits after the pinned Scene/intro. Without a low refresh priority
        // ScrollTrigger measures this start against pre-pin geometry, so
        // 'top 85%' maps to the wrong scroll point and (being `once`) fires in a
        // dead zone before it's ever on screen. Matches interlude.js / survey.js.
        invalidateOnRefresh: true,
        refreshPriority: -1
      }
    });

    tl.to(items, {
      y: 0,
      autoAlpha: 1,
      filter: 'blur(0px)',
      duration: 0.9,
      ease: 'power3.out',
      stagger: 0.12
    });

    tweens.push(tl);
    if (tl.scrollTrigger) triggers.push(tl.scrollTrigger);
  }

  function initAll() {
    gsap.registerPlugin(ScrollTrigger);
    document.querySelectorAll('[data-final-cta]').forEach(setup);
  }

  whenReady(initAll);

  // Recalculate once images/fonts have settled.
  window.addEventListener('load', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  // Shopify theme editor: tear down + rebuild when the section reloads, so
  // changing a setting never leaves a button stuck at its hidden state.
  document.addEventListener('shopify:section:load', function (e) {
    if (!ready()) return;
    if (!e.target.querySelector('[data-final-cta]')) return;
    reset();
    document.querySelectorAll('[data-final-cta]').forEach(setup);
  });
})();
